import { useAbility } from '@casl/react';
import { AbilityContext } from '../context/casl/AbilityContext';
import React from 'react';

interface CanProps {
  I: string; // action
  a: string; // subject
  passThrough?: React.ReactNode; // content to render if permission is denied
  children: React.ReactNode;
}

/**
 * A declarative component that renders its children only if the current
 * user has the required permission. It is bound to the dynamic AbilityContext.
 *
 * This component mimics the API of the one created by `createCanBoundTo`
 * but works with a dynamic, context-provided ability object.
 *
 * Example:
 * <Can I="create" a="person">
 *   <Button>Create User</Button>
 * </Can>
 */
export const Can: React.FC<CanProps> = ({ I: action, a: subject, passThrough, children }) => {
  const ability = useAbility(AbilityContext);

  // Check if the user has the permission for the given action and subject
  if (ability.can(action, subject)) {
    return <>{children}</>;
  }

  // If the user does not have permission, render passThrough if provided, otherwise render nothing.
  return passThrough ? <>{passThrough}</> : null;
};
